import { Lock, ShieldCheck, Globe, FileBadge } from "lucide-react";
import { Emblem } from "./Emblem";
import { FirmSignature } from "./FirmSignature";
import { useLanguage } from "@/i18n/LanguageContext";
import { QUICK_LINKS } from "./data";

const BADGES = [
  { icon: ShieldCheck, text: "ISO/IEC 27001:2022" },
  { icon: Lock, text: "GDPR Art. 32 Compliant" },
  { icon: Globe, text: "NY Convention 1958" },
  { icon: FileBadge, text: "eIDAS Qualified" },
];

export const Footer = ({ onPortal }) => {
  const { t } = useLanguage();
  return (
    <footer data-testid="site-footer" className="border-t border-gold/15 bg-navy-deep">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center text-gold">
                <Emblem className="h-11 w-10" />
              </span>
              <div>
                <div className="font-serif text-xl font-semibold tracking-wide text-slate-100">
                  LEXAEGIS <span className="font-mono text-[10px] font-normal uppercase tracking-[0.3em] text-gold">International</span>
                </div>
                <div className="mt-0.5 text-[10px] tracking-[0.1em] text-slate-400">{t.brandSub}</div>
              </div>
            </div>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-slate-400">{t.footer.compliance}</p>
            <div className="mt-7 flex flex-wrap gap-2.5" data-testid="compliance-badges">
              {BADGES.map((b) => (
                <span key={b.text} className="inline-flex items-center gap-2 border border-navy-border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-300">
                  <b.icon className="h-3.5 w-3.5 text-gold" /> {b.text}
                </span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3">
            <h4 className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">{t.footer.institutional}</h4>
            <ul className="mt-5 space-y-3">
              {QUICK_LINKS.map((l, i) => (
                <li key={l.href}>
                  <a href={l.href} data-testid={l.testid}
                    className="text-sm text-slate-400 transition-colors duration-300 hover:text-gold">
                    {t.footer.links[i]}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h4 className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">{t.footer.secureTitle}</h4>
            <p className="mt-5 text-sm leading-relaxed text-slate-400">{t.footer.secureBody}</p>
            <button onClick={onPortal} data-testid="footer-portal-login-button"
              className="mt-6 inline-flex items-center gap-3 border border-gold/50 px-6 py-3.5 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-navy-deep">
              <Lock className="h-4 w-4" /> {t.footer.portalBtn}
            </button>
            <p className="mt-6 border-l-2 border-gold/40 pl-4 text-xs italic leading-relaxed text-slate-500">
              {t.footer.ethics}
            </p>
          </div>
        </div>

        <div className="mt-16 border-t border-navy-border pt-12 text-center" data-testid="footer-signature-block">
          <div className="gold-hairline mx-auto w-32" />
          <FirmSignature size="text-3xl sm:text-4xl lg:text-5xl" className="mt-8" testid="footer-firm-signature" />
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
            {t.footer.sovereign}
          </p>
          <div className="gold-hairline mx-auto mt-8 w-32" />
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-navy-border pt-7 lg:flex-row">
          <p className="text-center font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500" data-testid="footer-copyright">
            {t.footer.copyright}
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-gold/60">
            GDPR · ISO 27001 · eIDAS · NY Convention 1958
          </p>
        </div>
      </div>
    </footer>
  );
};
